'use client';
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CanvasRevealEffect } from '@components/ui/Canvas-reveal-effect';
import { Button } from '@components/MovingBorders';
import { IconBase } from 'react-icons/lib';

interface CardProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  details: string[];
  children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({ title, icon, description, details, children }) => {
  const [hovered, setHovered] = React.useState<boolean>(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="border border-white/[0.1] group/canvas-card flex items-center justify-center max-w-sm w-full mx-auto p-6 relative h-[32rem] backdrop-blur-sm hover:border-purple-500/50 transition-all duration-500 rounded-xl bg-black-100/50"
    >
      <IconBase className="absolute h-6 w-6 -top-3 -left-3 dark:text-white text-black opacity-30" />
      <IconBase className="absolute h-6 w-6 -bottom-3 -left-3 dark:text-white text-black opacity-30" />
      <IconBase className="absolute h-6 w-6 -top-3 -right-3 dark:text-white text-black opacity-30" />
      <IconBase className="absolute h-6 w-6 -bottom-3 -right-3 dark:text-white text-black opacity-30" />

      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full w-full absolute inset-0 rounded-xl overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-20 flex flex-col items-center px-2">
        <div className="text-center group-hover/canvas-card:-translate-y-4 transition duration-200 w-full mx-auto flex items-center justify-center">
          {icon}
        </div>
        <h2 className="dark:text-white text-xl opacity-100 relative z-10 text-black mt-4 font-bold group-hover/canvas-card:text-white group-hover/canvas-card:-translate-y-2 transition duration-200 text-center">
          {title}
        </h2>
        <p className="mt-3 text-sm text-gray-400 opacity-100 group-hover/canvas-card:opacity-0 transition-all duration-300 text-center leading-relaxed">
          {description}
        </p>
        <ul className="mt-4 space-y-2 opacity-0 group-hover/canvas-card:opacity-100 transition-all duration-500 delay-100">
          {details.map((detail, i) => (
            <li key={i} className="text-xs text-white/90 flex items-start gap-2">
              <span className="text-purple-400 mt-0.5">▸</span>
              {detail}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Approach = () => {
  const developmentSteps = [
    {
      title: 'Agentic Architecture',
      description:
        'I design systems where AI agents handle the grunt work — scaffolding, boilerplate, and research — while I focus on decisions that matter.',
      details: [
        'Multi-agent orchestration for parallel task execution',
        'LLM-assisted system design & architecture drafts',
        'Prompt-driven prototyping before writing a single line',
        'Human-in-the-loop validation at every critical junction'
      ],
      step: 1,
      colors: [
        [147, 51, 234],
        [79, 70, 229]
      ]
    },
    {
      title: 'Hardened Development',
      description:
        'I write code alongside AI copilots but never trust blindly. Every suggestion gets reviewed, tested, and stress-tested before it ships.',
      details: [
        'AI pair-programming with strict code review gates',
        'Adversarial testing against LLM-generated outputs',
        'Guardrails & sandboxing for AI-produced code paths',
        'Continuous refactoring driven by static analysis + AI insights'
      ],
      step: 2,
      colors: [
        [59, 130, 246],
        [147, 51, 234]
      ]
    },
    {
      title: 'Cyber-Aware LLM Ops',
      description:
        'AI in production means new attack surfaces. I treat prompt injection, data leakage, and model abuse as first-class security concerns.',
      details: [
        'Prompt injection defense & input sanitization layers',
        'Output filtering to prevent data exfiltration',
        'Rate limiting & abuse detection on AI endpoints',
        'Model output validation & hallucination detection'
      ],
      step: 3,
      colors: [
        [16, 185, 129],
        [59, 130, 246]
      ]
    },
    {
      title: 'Orchestration & Deployment',
      description:
        'Shipping AI-enhanced apps requires thoughtful infra — model versioning, fallback strategies, and observability baked in from day one.',
      details: [
        'AI model versioning & canary deployments',
        'Fallback chains when models degrade or timeout',
        'Observability dashboards for token usage & latency',
        'Infrastructure as Code with AI cost guardrails'
      ],
      step: 4,
      colors: [
        [245, 158, 11],
        [16, 185, 129]
      ]
    },
    {
      title: 'Nexus Guard',
      description:
        'My personal quality gate — an AI-powered validation layer that catches what humans miss before anything touches production.',
      details: [
        'Automated security scanning on every PR',
        'AI-driven code smell & complexity detection',
        'Dependency risk scoring & supply chain audits',
        'Performance regression detection via synthetic benchmarks'
      ],
      step: 5,
      colors: [
        [236, 72, 153],
        [147, 51, 234]
      ]
    }
  ];

  return (
    <div className="py-20">
      <div className="flex flex-col items-center text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-sans text-4xl lg:text-5xl font-bold z-10 mb-6"
        >
          <span>How I </span>
          <span className="text-purple">Build</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="max-w-2xl mb-16 text-gray-400 text-lg"
        >
          Senior engineering meets agentic AI — I orchestrate LLMs as tools, not crutches, while keeping security and
          quality non-negotiable
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 w-full max-w-7xl mx-auto px-4">
          {developmentSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
            >
              <Card
                title={step.title}
                description={step.description}
                details={step.details}
                icon={
                  <Button className="p-3 lg:text-2xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg shadow-purple-500/25">
                    {step.step}
                  </Button>
                }
              >
                <CanvasRevealEffect animationSpeed={2} colors={step.colors} dotSize={2} />
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Approach;
